import { ReactNode } from 'react';

export function PageHead({ title, sub, action }: { title: string; sub?: string; action?: ReactNode }) {
  return (
    <div className="px-5 md:px-8 pt-6 md:pt-10 pb-4 flex items-end justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">{title}</h1>
        {sub && <p className="text-muted text-sm mt-1">{sub}</p>}
      </div>
      {action}
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <div className={`glass rounded-2xl p-4 md:p-5 ${className}`}>{children}</div>;
}

export function Pill({ tone = 'muted', children }: { tone?: 'good' | 'warn' | 'danger' | 'accent' | 'muted'; children: ReactNode }) {
  const c =
    tone === 'good' ? 'bg-good/15 text-good ring-good/30' :
    tone === 'warn' ? 'bg-warn/15 text-warn ring-warn/30' :
    tone === 'danger' ? 'bg-danger/15 text-danger ring-danger/30' :
    tone === 'accent' ? 'bg-accent/15 text-accent ring-accent/30' :
    'bg-elevated text-muted ring-line';
  return <span className={`inline-flex items-center px-2 h-6 rounded-md text-[11px] font-medium tabular-nums ring-1 ring-inset ${c}`}>{children}</span>;
}

export function Loading({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2 px-5 md:px-8">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="shimmer rounded-xl h-14" />
      ))}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="px-5 md:px-8 py-10 text-center">
      <div className="text-lg font-medium">{title}</div>
      {hint && <div className="text-muted text-sm mt-1">{hint}</div>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, retry }: { error: Error | string | unknown; retry?: () => void }) {
  const msg = error instanceof Error ? error.message : String(error);
  const isAuth = /503|RSME_API_KEY|Bearer|backend proxy|Network error/i.test(msg);
  return (
    <div className="mx-5 md:mx-8 my-4 rounded-xl border border-danger/30 bg-danger/5 p-4">
      <div className="font-medium text-danger">{isAuth ? 'Backend not connected' : 'Something broke'}</div>
      <div className="text-sm text-muted mt-1">
        {isAuth
          ? 'The RSME API key has not been wired into this deploy. Ask Ian for RSME_API_KEY and set it via `wrangler pages secret put RSME_API_KEY --project-name=rqdoc`.'
          : msg}
      </div>
      {retry && (
        <button onClick={retry} className="mt-3 h-8 px-3 rounded-lg bg-elevated hover:bg-line text-sm">Retry</button>
      )}
    </div>
  );
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`h-10 px-3 rounded-lg bg-elevated border border-line focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/30 text-sm w-full ${props.className ?? ''}`}
    />
  );
}

export function Button({ tone = 'primary', children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement> & { tone?: 'primary' | 'ghost' | 'danger' }) {
  const c =
    tone === 'primary' ? 'bg-accent text-bg hover:bg-accent/90' :
    tone === 'danger' ? 'bg-danger/15 text-danger hover:bg-danger/25' :
    'bg-elevated text-ink hover:bg-line';
  return (
    <button
      {...rest}
      className={`h-10 px-4 rounded-lg text-sm font-medium transition-colors ${c} disabled:opacity-50 disabled:cursor-not-allowed ${rest.className ?? ''}`}
    >{children}</button>
  );
}

export function StatusDot({ tone }: { tone: 'good' | 'warn' | 'danger' | 'muted' }) {
  const c = tone === 'good' ? 'bg-good' : tone === 'warn' ? 'bg-warn' : tone === 'danger' ? 'bg-danger' : 'bg-muted';
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${c}`} />;
}
